import { z } from "zod";
import { platformRoleSchema } from "./common";

export const gymSchema = z.object({
  id: z.number(),
  publicId: z.string(),
  slug: z.string(),
  name: z.string(),
  rut: z.string().nullish(),
  legalName: z.string().nullish(),
  address: z.string().nullish(),
  country: z.string().nullish(),
  timezone: z.string(),
  active: z.boolean(),
  logoKey: z.string().nullish(),
  logoKeyEmail: z.string().nullish(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
});
export type Gym = z.infer<typeof gymSchema>;

const taxConfigSchema = z.object({
  type: z.enum(["vat", "none", "custom"]),
  value: z.number().optional(),
});

const commissionSettingsSchema = z.object({
  percentage: z.number(),
  fixedFee: z.number(),
  tax: taxConfigSchema,
  calculationBase: z.enum(["TOTAL_GROSS", "TOTAL_NET"]),
});

const paymentMethodAdditionalSettingsSchema = z
  .object({
    commission: commissionSettingsSchema.optional(),
    logo_url: z.string().optional(),
  })
  .optional();

const gymPaymentMethodSchema = z.object({
  publicId: z.string(),
  name: z.string(),
  key: z.string(),
  type: z.enum(["manual", "gateway"]).default("manual"),
  ticketConfig: z.object({
    plan: z.object({ requires: z.boolean() }),
    food: z.object({ requires: z.boolean() }),
    merchandise: z.object({ requires: z.boolean() }),
  }),
  active: z.boolean(),
  additionalSettings: paymentMethodAdditionalSettingsSchema,
});

const gymPlanCategorySchema = z.object({
  publicId: z.string(),
  name: z.string(),
  key: z.string(),
  isClassBased: z.boolean(),
  planNames: z.array(z.string()).default([]),
});

export const gymDetailSchema = gymSchema.extend({
  emailDailyLimit: z.number().nullable().default(null),
  paymentMethods: z.array(gymPaymentMethodSchema),
  planCategories: z.array(gymPlanCategorySchema),
});
export type GymDetail = z.infer<typeof gymDetailSchema>;
export type GymPaymentMethod = z.infer<typeof gymPaymentMethodSchema>;
export type GymPlanCategory = z.infer<typeof gymPlanCategorySchema>;

export const updateGymInputSchema = z.object({
  name: z.string().optional(),
  rut: z.string().nullish(),
  legalName: z.string().nullish(),
  address: z.string().nullish(),
  country: z.string().nullish(),
  timezone: z.string().optional(),
  active: z.boolean().optional(),
});
export type UpdateGymInput = z.infer<typeof updateGymInputSchema>;

export const createAdminPaymentMethodInputSchema = z.object({
  name: z.string().min(1),
  key: z.string().min(1),
  ticketConfig: z.object({
    plan: z.object({ requires: z.boolean() }),
    food: z.object({ requires: z.boolean() }),
    merchandise: z.object({ requires: z.boolean() }),
  }),
});
export type CreateAdminPaymentMethodInput = z.infer<
  typeof createAdminPaymentMethodInputSchema
>;

export const updateAdminPaymentMethodInputSchema = z.object({
  name: z.string().min(1).optional(),
  ticketConfig: z
    .object({
      plan: z.object({ requires: z.boolean() }).optional(),
      food: z.object({ requires: z.boolean() }).optional(),
      merchandise: z.object({ requires: z.boolean() }).optional(),
    })
    .optional(),
  additionalSettings: z
    .object({
      commission: commissionSettingsSchema.optional(),
    })
    .nullable()
    .optional(),
});
export type UpdateAdminPaymentMethodInput = z.infer<
  typeof updateAdminPaymentMethodInputSchema
>;

export const createAdminPlanCategoryInputSchema = z.object({
  name: z.string().min(1),
  key: z.string().min(1).optional(),
  isClassBased: z.boolean().optional(),
});
export type CreateAdminPlanCategoryInput = z.infer<
  typeof createAdminPlanCategoryInputSchema
>;

export const updateAdminPlanCategoryInputSchema = z.object({
  name: z.string().min(1).optional(),
  key: z.string().min(1).optional(),
  isClassBased: z.boolean().optional(),
});
export type UpdateAdminPlanCategoryInput = z.infer<
  typeof updateAdminPlanCategoryInputSchema
>;

export const adminUserSchema = z.object({
  publicId: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  role: platformRoleSchema,
  gymName: z.string(),
  gymPublicId: z.string(),
  createdAt: z.string(),
});
export type AdminUser = z.infer<typeof adminUserSchema>;

export const createGymInputSchema = z.object({
  slug: z.string(),
  name: z.string(),
  rut: z.string().optional(),
  legalName: z.string().optional(),
  address: z.string().optional(),
  country: z.string().optional(),
  active: z.boolean().optional(),
});
export type CreateGymInput = z.infer<typeof createGymInputSchema>;

export const createAdminUserInputSchema = z.object({
  email: z.string().email(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  role: platformRoleSchema,
  gymId: z.number(),
});
export type CreateAdminUserInput = z.infer<typeof createAdminUserInputSchema>;

const adminReportExtraConfigSchema = z.object({
  expiringSoonDays: z.number().optional(),
  recentlyExpiredDays: z.number().optional(),
});

const subscriptionRemindersExcludeSchema = z.object({
  paymentMethodKeys: z.array(z.string()).optional(),
});

const emailReportConfigSchema = z.object({
  enabled: z.boolean(),
  time: z.string(),
  subject: z.string(),
  recipients: z.array(z.string()),
  config: adminReportExtraConfigSchema.optional(),
  exclude: subscriptionRemindersExcludeSchema.optional(),
});

export const DEFAULT_INACTIVITY_DAYS = 14;
export const MIN_INACTIVITY_DAYS = 3;
export const MAX_INACTIVITY_DAYS = 180;

const lowStockAlertsConfigSchema = z.object({
  enabled: z.boolean(),
  subject: z.string(),
  recipients: z.array(z.string()),
});

export const gymEmailConfigSchema = z.object({
  adminReport: emailReportConfigSchema,
  salesReport: emailReportConfigSchema,
  subscriptionReminders: emailReportConfigSchema,
  lowStockAlerts: lowStockAlertsConfigSchema,
  paymentFailed: z.object({ enabled: z.boolean() }).default({ enabled: true }),
  inactivityReminders: z
    .object({
      enabled: z.boolean(),
      time: z.string(),
      subject: z.string(),
      inactiveDays: z.number().int(),
    })
    .default({
      enabled: false,
      time: "",
      subject: "",
      inactiveDays: DEFAULT_INACTIVITY_DAYS,
    }),
  gymPublicId: z.string().optional(),
});

export type GymEmailConfig = z.infer<typeof gymEmailConfigSchema>;
export const gymEmailConfigPartialSchema = gymEmailConfigSchema.partial();
export type UpdateGymEmailConfigInput = z.infer<
  typeof gymEmailConfigPartialSchema
>;

// --- Form validation schemas (used by legacy validators below) ---
const adminUserFormSchema = z.object({
  email: z.string().min(1, "error_email_required").email("error_email_invalid"),
  gymPublicId: z.string().min(1, "error_gym_required"),
});

const slugSchema = z
  .string()
  .min(1, "error_slug_required")
  .refine(
    (val) => /^[a-z0-9_-]+$/.test(val.toLowerCase()),
    "error_slug_invalid",
  );

const gymFormSchemaBase = z.object({
  slug: slugSchema,
  name: z.string().min(1, "error_name_required"),
});

export type AdminUserFormValidationResult = {
  isValid: boolean;
  errorKey:
    | "error_email_required"
    | "error_email_invalid"
    | "error_gym_required"
    | null;
};

export type GymFormValidationResult = {
  isValid: boolean;
  errorKey:
    | "error_slug_required"
    | "error_slug_invalid"
    | "error_name_required"
    | "error_slug_exists"
    | null;
};

/** Validates admin user form; returns result with errorKey for i18n. */
export function validateAdminUserForm(form: {
  email: string;
  gymPublicId: string | "";
}): AdminUserFormValidationResult {
  const parsed = adminUserFormSchema.safeParse({
    ...form,
    gymPublicId: form.gymPublicId === "" ? "" : form.gymPublicId,
  });
  if (parsed.success) return { isValid: true, errorKey: null };
  const first = parsed.error.issues[0];
  const msg = first?.message;
  const errorKey =
    typeof msg === "string" && msg.startsWith("error_")
      ? (msg as AdminUserFormValidationResult["errorKey"])
      : "error_email_required";
  return { isValid: false, errorKey };
}

export function validateGymForm(
  form: { slug: string; name: string },
  existingGyms: Array<{ slug: string }>,
): GymFormValidationResult {
  const slugLower = form.slug.toLowerCase();
  if (!/^[a-z0-9_-]+$/.test(slugLower))
    return { isValid: false, errorKey: "error_slug_invalid" };
  if (existingGyms.some((g) => g.slug.toLowerCase() === slugLower))
    return { isValid: false, errorKey: "error_slug_exists" };
  const parsed = gymFormSchemaBase.safeParse(form);
  if (parsed.success) return { isValid: true, errorKey: null };
  const first = parsed.error.issues[0];
  const msg = first?.message;
  const errorKey =
    typeof msg === "string" && msg.startsWith("error_")
      ? (msg as GymFormValidationResult["errorKey"])
      : "error_slug_required";
  return { isValid: false, errorKey };
}
